import React from 'react';
import { Link } from 'react-router-dom';
import { Info, Globe2, TrendingUp, Sparkles } from 'lucide-react';
import ContainerPageLayout, { CodeBlock, ReqBadge, DownloadButtons } from '../components/ContainerPageLayout';
import ApiPlayground from '../components/ApiPlayground';

const TOC = [
  { id: 'overview', label: 'Overview' },
  { id: 'process', label: 'DCC Query Process' },
  { id: 'making-request', label: 'Making The Request', children: [
    { id: 'sample-request-url', label: 'Sample Request URL' },
    { id: 'sample-csharp', label: 'Sample DCC Query Request' },
    { id: 'request-reference', label: 'Request Reference' },
  ]},
  { id: 'available-form-fields', label: 'Available Form Fields' },
  { id: 'validation', label: 'Field Validation' },
  { id: 'sample-xml', label: 'Sample DCC Query XML' },
  { id: 'data-returned', label: 'Data Returned' },
  { id: 'expected-responses', label: 'Expected XML Documents' },
  { id: 'applying-dcc', label: 'Applying DCC to a Sale' },
];

const REQUEST_URL_SAMPLE = `https://staging.ecomm365.com/acqapi/service.ashx?Username=SomeName&Password=SomePassword&MessageID=30dd879c-ee2f-11db-8314-0800200c9a66&APISignature=dccquery&Data=<R>...</R>`;

const CSHARP_SAMPLE = `[HttpPost]
public IActionResult index()
{
    HttpWebRequest httpWReq = (HttpWebRequest)WebRequest.Create("https://staging.ecomm365.com/acqapi/service.ashx");
    UTF8Encoding encoding = new UTF8Encoding();

    string pData = "username=SomeName";
    pData += "&password=SomePassword";
    pData += "&messageId=" + System.Guid.NewGuid().ToString();
    pData += "&ApiSignature=DccQuery"; //API Signature - DCC Query
    pData += "&Data=" + WebUtility.UrlEncode(@"<R>...</R>");

    byte[] data = encoding.GetBytes(pData);
    httpWReq.Method = "POST";
    httpWReq.ContentType = "application/x-www-form-urlencoded";
    httpWReq.ContentLength = data.Length;

    byte[] byteArray = Encoding.UTF8.GetBytes(pData);
    Stream dataStream = httpWReq.GetRequestStream();
    dataStream.Write(byteArray, 0, byteArray.Length);
    dataStream.Close();

    HttpWebResponse response = (HttpWebResponse)httpWReq.GetResponse();
    string responseString = new StreamReader(response.GetResponseStream()).ReadToEnd();

    XmlDocument xDoc = new XmlDocument();
    xDoc.LoadXml(responseString);

    XmlNodeList ResponseR1 = xDoc.GetElementsByTagName("R1");
    XmlNodeList ResponseR3 = xDoc.GetElementsByTagName("R3"); //DCC Token
    //... etc

    return View();
}`;

const DCC_XML = `<!-- DCC Query Request Demonstration -->
<R>
    <R1>EUR</R1>
    <R2>1000596</R2>
    <R3>1000</R3>
    <R4>1234567812341234</R4>
</R>`;

const RESP_0000 = `<!-- Sample Server Response, XML Document -->
<?xml version="1.0"?>
<R>
    <R1>0000</R1>
    <R2>DCC Conversion Successful</R2>
    <R3>55b44e3d-f05e-4fe0-b85a-62c677bdd1a3</R3>
    <R4>2018-10-03T13:20:02.154878+01:00</R4>
    <R5>1000</R5>
    <R6>978</R6>
    <R7>1222</R7>
    <R8>840</R8>
    <R9>1.22178</R9>
    <R10>1.22178</R10>
    <R11>4.30</R11>
    <R12>0</R12>
    <R13>978</R13>
    <R14>2</R14>
</R>`;

const SALE_APPLY_XML = `<!-- Applying DCC In A Sale Request Demonstration -->
<?xml version="1.0"?>
<R>
    <R1>...</R1>
    ...
    <R45>...</R45>
    <DCC>
        <TKN>55b44e3d-f05e-4fe0-b85a-62c677bdd1a3</TKN>
    </DCC>
</R>`;

const FORM_FIELDS = [
  { name: 'R1', desc: (<>Current Alpha Currency Code of the Vendor (see <Link to="/technical-documentation/appendix/currency-codes" className="text-[#1F4E79] underline hover:text-[#163a5f] font-semibold">Currency Codes</Link> for the list of Alpha Currency Codes).</>), req: 'Y', def: 'AN(3)' },
  { name: 'R2', desc: 'Vendor ID.', req: 'Y', def: 'AN(50)' },
  { name: 'R3', desc: 'Sale Amount (without decimals). E.g. €100.00 = [10000], €123.67 = [12367], €0.99 = [99].', req: 'Y', def: 'N(20)' },
  { name: 'R4', desc: 'Card Number.', req: 'Y', def: 'N(19)' },
];

const VALIDATION = [
  { name: 'R1', desc: 'Current Alpha Currency Code of the Vendor.', regex: '^[A-Za-z0-9]{3}$' },
  { name: 'R2', desc: 'Vendor ID.',                                  regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R3', desc: 'Sale Amount (without decimals).',             regex: '^[0-9]{0,20}$' },
  { name: 'R4', desc: 'Card Number.',                                regex: 'Luhn Algorithm' },
];

const RESP_FIELDS = [
  { name: 'R1',  desc: (<>Error Code (see <Link to="/technical-documentation/appendix/error-codes" className="text-[#1F4E79] underline hover:text-[#163a5f] font-semibold">Error Codes</Link> for a list of available error codes).</>), def: 'N(4)' },
  { name: 'R2',  desc: 'Description — human-readable message paired with R1.', def: 'AN(250)' },
  { name: 'R3',  desc: 'DCC Token — identifies the DCC conversion within the Sale Request.', def: 'AN(250)' },
  { name: 'R4',  desc: 'DCC Conversion Date/Time — when the conversion took place.', def: 'AN(250)' },
  { name: 'R5',  desc: 'Sale Amount.', def: 'AN(250)' },
  { name: 'R6',  desc: 'Numeric Currency Code of the Vendor.', def: 'AN(250)' },
  { name: 'R7',  desc: 'Sale Amount converted with DCC.', def: 'AN(250)' },
  { name: 'R8',  desc: 'Numeric Currency Code of the converted Sale Amount.', def: 'AN(250)' },
  { name: 'R9',  desc: 'Exchange Rate.', def: 'AN(250)' },
  { name: 'R10', desc: 'Markup Exchange Rate.', def: 'AN(250)' },
  { name: 'R11', desc: 'Markup Percentage.', def: 'AN(250)' },
  { name: 'R12', desc: 'Card Issuer Country — reserved for future use, will return 0 for now.', def: 'AN(250)' },
  { name: 'R13', desc: 'Clearing Numeric Currency Code.', def: 'AN(250)' },
  { name: 'R14', desc: 'DCC Valid Hours — number of hours the DCC conversion will remain valid.', def: 'AN(250)' },
];

/* ----- Visual DCC flow ----- */
const ProcessFlow = () => {
  const steps = [
    { n: 1, t: 'Enable DCC', d: 'Contact eCOMM365 to opt-in to DCC and configure the currencies you wish to accept.' },
    { n: 2, t: 'DCC Query',  d: 'Send the cardholder\'s PAN + amount + Vendor ID. A successful response returns 0000 with a DCC Token (R3) plus live exchange rate details.' },
    { n: 3, t: 'Apply Token in Sale', d: 'Attach the DCC Token inside a <DCC> element on your Sale request. The cardholder is charged in their home currency; you settle in yours.' },
  ];
  return (
    <ol className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="dcc-process-flow">
      {steps.map((s, i) => (
        <li key={s.n} className="relative rounded-lg border-2 border-[#1F4E79]/15 bg-gradient-to-br from-white to-blue-50/40 p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center justify-center w-7 h-7 bg-[#1F4E79] text-white rounded-full text-xs font-bold">{s.n}</span>
            <h4 className="text-sm font-bold text-gray-900">{s.t}</h4>
          </div>
          <p className="text-xs text-gray-600 leading-relaxed">{s.d}</p>
          {i < steps.length - 1 && (
            <span className="hidden md:block absolute -right-2 top-1/2 -translate-y-1/2 text-[#1F4E79] text-xl font-bold select-none">&rsaquo;</span>
          )}
        </li>
      ))}
    </ol>
  );
};

const DCC = () => (
  <ContainerPageLayout
    currentId="dcc"
    title="DCC"
    subtitle={
      <>
        <strong>Dynamic Currency Conversion</strong> — let international Visa &amp; Mastercard cardholders pay in their own currency at checkout, while you settle in your domestic currency.
      </>
    }
    titleBadge="DCC Query"
    signature="dccquery"
    toc={TOC}
    mainTestId="dcc-page"
    sidebarTestId="dcc-sidebar"
  >
    {/* Overview */}
    <section id="overview" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Overview</h2>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-gray-100 text-gray-700 border border-gray-200 uppercase tracking-wide" data-testid="dcc-meaning-chip">
          <Globe2 size={12} />
          Dynamic Currency Conversion
        </span>
      </div>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        The <strong>DCC Query</strong> API is only required if the merchant wants to offer DCC via the{' '}
        <Link to="/technical-documentation/api-reference/sale-pre-auth" className="text-[#1F4E79] underline hover:text-[#163a5f]">Sale API</Link>. Our{' '}
        <Link to="/technical-documentation/api-reference/hosted-payment-page" className="text-[#1F4E79] underline hover:text-[#163a5f]">Hosted Payments Page API</Link> handles DCC automatically if requested to do so.
      </p>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        <strong>Dynamic Currency Conversion (DCC)</strong> is the user-friendly, point-of-purchase service whereby international Visa &amp; Mastercard card users can choose to pay in their own currency, rather than the domestic currency of where they are making their purchase.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-5">
        <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white/70 p-4">
          <Globe2 size={20} className="text-[#1F4E79] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-gray-900">Home-currency Checkout</p>
            <p className="text-xs text-gray-600 mt-1">International cardholders see the total in their own currency — reduces cart abandonment.</p>
          </div>
        </div>
        <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white/70 p-4">
          <TrendingUp size={20} className="text-[#1F4E79] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-gray-900">Live Exchange Rate</p>
            <p className="text-xs text-gray-600 mt-1">Response returns the exchange rate, markup rate, markup % and validity hours of the quote.</p>
          </div>
        </div>
        <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white/70 p-4">
          <Sparkles size={20} className="text-[#1F4E79] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-gray-900">Single DCC Token</p>
            <p className="text-xs text-gray-600 mt-1">Attach the returned token to your Sale request inside a <code className="bg-white/70 px-1 rounded">&lt;DCC&gt;</code> element — nothing else to change.</p>
          </div>
        </div>
      </div>
    </section>

    {/* Process */}
    <section id="process" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">DCC Query Process</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-6">
        The process of the DCC Query API is simple and straightforward. In order to successfully cater for DCC, please ensure you make the DCC Query request <strong>before</strong> making the sale request — you must supply additional fields into the sale request.
      </p>

      <ProcessFlow />

      <ol className="list-decimal pl-6 space-y-3 text-gray-700 text-sm leading-relaxed mt-6">
        <li>The merchant decides whether to offer DCC to the consumer and provides this information to eCOMM365.</li>
        <li>The merchant makes a request using our DCC Query API including the consumer's Card Number, Amount &amp; Vendor ID. If DCC is applicable, the response includes <strong className="text-[#c32222]">0000 — DCC Conversion Successful</strong> and the DCC Token which will be required within the Sale API request.</li>
        <li>If DCC was successful and the merchant receives the DCC Token, the merchant must supply this DCC Token into the sale request in order to accept DCC.</li>
      </ol>
    </section>

    {/* Making The Request */}
    <section id="making-request" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Making The Request</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-3">
        HTTP is used as the request-response protocol between a merchant's site and the eCOMM API. In the back end, a merchant submits a HTTP POST request to the eCOMM server, which then returns an XML document where the merchant must parse the data inside and act accordingly.
      </p>
      <p className="text-gray-700 text-base leading-relaxed mb-3">
        The request string that is sent for the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">DCC Query</code> call must be composed of the following information:
      </p>
      <ol className="list-decimal pl-6 space-y-1 text-gray-700 text-sm mb-6">
        <li><strong>Username</strong> = SomeName</li>
        <li><strong>Password</strong> = SomePassword</li>
        <li><strong>MessageID</strong> = <em>*GUID (e.g. 30dd879c-ee2f-11db-8314-0800200c9a66)</em></li>
        <li><strong>APISignature</strong> = DccQuery</li>
        <li><strong>Data</strong> = Form data in XML format</li>
      </ol>

      <div id="sample-request-url" className="scroll-mt-28">
        <h3 className="text-lg font-bold text-gray-900 mb-2">A fully formatted HTTP POST request should look like this:</h3>
        <CodeBlock code={REQUEST_URL_SAMPLE} lang="http" testid="request-url-code" />
      </div>

      <div id="sample-csharp" className="scroll-mt-28 mt-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h3 className="text-xl font-bold text-gray-900">Sample <code className="bg-gray-100 px-2 py-0.5 rounded text-base font-mono">DCC Query</code> Request</h3>
          <DownloadButtons />
        </div>
        <CodeBlock code={CSHARP_SAMPLE} lang="csharp" testid="sample-csharp-code" />
      </div>

      <div id="request-reference" className="scroll-mt-28 mt-10">
        <ApiPlayground
          apiSignature="dccquery"
          title="Request Reference — DCC Query"
          description="Query a DCC quote for a given card, amount and vendor. A successful response returns 0000 with the DCC Token in R3."
          defaultData={DCC_XML.replace('<!-- DCC Query Request Demonstration -->\n', '')}
        />
      </div>
    </section>

    {/* Form Fields */}
    <section id="available-form-fields" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Available Form Data Fields</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        Below is a table containing all the available fields for passing POST data into the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">data</code> parameter within the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">DCC Query</code> request.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse" data-testid="form-fields-table">
          <thead>
            <tr className="bg-[#1F4E79] text-white">
              <th className="text-left p-3 w-20">Field</th>
              <th className="text-left p-3">Description</th>
              <th className="text-left p-3 w-24">Required</th>
              <th className="text-left p-3 w-28">Definition</th>
            </tr>
          </thead>
          <tbody>
            {FORM_FIELDS.map((f, i) => (
              <tr key={f.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                <td className="p-3 font-mono font-semibold text-[#1F4E79]">{f.name}</td>
                <td className="p-3">{f.desc}</td>
                <td className="p-3"><ReqBadge value={f.req} /></td>
                <td className="p-3 font-mono text-xs">{f.def}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>

    {/* Validation */}
    <section id="validation" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Available Form Data Fields — Validation</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        Below is a table containing all the available fields for the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">data</code> parameter within the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">DCC Query</code> request including its validation.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-[#1F4E79] text-white">
              <th className="text-left p-3 w-20">Field</th>
              <th className="text-left p-3">Description</th>
              <th className="text-left p-3">Validation</th>
            </tr>
          </thead>
          <tbody>
            {VALIDATION.map((v, i) => (
              <tr key={v.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                <td className="p-3 font-mono font-semibold text-[#1F4E79]">{v.name}</td>
                <td className="p-3">{v.desc}</td>
                <td className="p-3 font-mono text-xs break-all">{v.regex}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>

    {/* Sample XML */}
    <section id="sample-xml" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
        Sample <code className="bg-gray-100 px-2 py-0.5 rounded text-2xl font-mono">DCC Query</code> XML Document
      </h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        The below sample demonstrates what is expected when passing POST data into the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">data</code> parameter.
      </p>
      <CodeBlock code={DCC_XML} lang="xml" testid="dcc-xml-code" />
    </section>

    {/* Data Returned */}
    <section id="data-returned" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Data Returned</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">Below are the expected XML fields returned from the request.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-[#1F4E79] text-white">
              <th className="text-left p-3 w-20">Field</th>
              <th className="text-left p-3">Description</th>
              <th className="text-left p-3 w-28">Definition</th>
            </tr>
          </thead>
          <tbody>
            {RESP_FIELDS.map((f, i) => (
              <tr key={f.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                <td className="p-3 font-mono font-semibold text-[#1F4E79]">{f.name}</td>
                <td className="p-3">{f.desc}</td>
                <td className="p-3 font-mono text-xs">{f.def}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>

    {/* Expected Responses */}
    <section id="expected-responses" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Expected XML Documents</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        This section demonstrates the expected XML document responses returned from the HTTP POST once the request has passed validations.
      </p>

      <div className="bg-blue-50/80 border border-blue-200 rounded-lg p-5 mb-6">
        <p className="font-semibold text-[#1F4E79] mb-3">Expected error code responses:</p>
        <ol className="list-decimal pl-5 text-sm text-gray-700 space-y-2">
          <li><strong className="text-[#c32222]">0000</strong> — Indicates a successful DCC Query. The response includes the DCC Token in <code className="bg-white/70 px-1 rounded">&lt;R3&gt;</code>, which will be required in the Sale request.</li>
        </ol>
      </div>

      <h3 className="text-lg font-bold text-gray-900 mb-2">Sample XML Response (0000)</h3>
      <CodeBlock code={RESP_0000} lang="xml" testid="resp-0000-code" />
    </section>

    {/* Applying DCC to a Sale */}
    <section id="applying-dcc" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Applying DCC to a Sale</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        This section demonstrates how to apply the generated DCC Token to the Sale Request once the request has passed validations.
      </p>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        After receiving the DCC Token which is returned in the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">&lt;R3&gt;</code> field of the XML Document, you must now supply this generated DCC Token when making the Sale Request. At the end of the XML Document within the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">data</code> parameter of the sale request, you must supply the following fields encapsulated inside the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">&lt;DCC&gt;</code>.
      </p>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        Below is an example that demonstrates how the XML Document should look when forming the Sale Query.
      </p>
      <CodeBlock code={SALE_APPLY_XML} lang="xml" testid="sale-apply-code" />

      <div className="bg-blue-50/80 border border-blue-200 rounded-lg p-4 flex items-start gap-3 mt-5">
        <Info size={18} className="text-[#1F4E79] flex-shrink-0 mt-0.5" />
        <p className="text-sm text-gray-700">
          <strong>Next step:</strong> head over to{' '}
          <Link to="/technical-documentation/api-reference/sale-pre-auth" className="text-[#1F4E79] underline hover:text-[#163a5f]">Sale &amp; Pre-Auth</Link>{' '}
          to see the full Sale request schema. Attach the DCC Token inside <code className="bg-white/70 px-1 rounded">&lt;DCC&gt;&lt;TKN&gt;…&lt;/TKN&gt;&lt;/DCC&gt;</code> at the end of your <code className="bg-white/70 px-1 rounded">&lt;R&gt;</code> payload.
        </p>
      </div>
    </section>
  </ContainerPageLayout>
);

export default DCC;
